import { useState, useEffect } from "react";
import { useResource } from "@/hooks/useResource";
import { EmptyState, LoadingState } from "@/components/common/States";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, ChevronDown, GitMerge, Trash2, ShieldCheck, Wand2, AlertTriangle } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const dt = (s) => (s ? String(s).slice(0, 10) : "—");

const CLS = {
  SURVIVOR: ["bg-emerald-100 text-emerald-700", "Keep (survivor)"],
  SAFE_DELETE: ["bg-sky-100 text-sky-700", "Safe to delete"],
  SAFE_MERGE: ["bg-amber-100 text-amber-700", "Safe to merge"],
  REVIEW: ["bg-rose-100 text-rose-700", "Requires review"],
};

function Stat({ label, value, tone = "stone", tid }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3 shadow-sm" data-testid={tid}>
      <div className={`font-display text-xl font-bold ${{ rose: "text-rose-600", amber: "text-amber-600", sky: "text-sky-600", emerald: "text-emerald-600" }[tone] || "text-stone-900"}`}>{value}</div>
      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</div>
    </div>
  );
}

function MergePreviewDialog({ entity, survivorId, duplicateId, open, onOpenChange, onDone }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const load = async () => {
    setData(null);
    try {
      const { data } = await api.get(`/dedup/${entity}/merge-preview?survivorId=${survivorId}&duplicateId=${duplicateId}`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); onOpenChange(false); }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, survivorId, duplicateId]);
  const confirm = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/dedup/${entity}/merge`, { survivorId, duplicateId, reason });
      toast.success(`Merged · ${res.data.totalMigrated} reference(s) moved to survivor`);
      onOpenChange(false); setReason(""); onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Merge failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-2xl" data-testid="dd-merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitMerge className="h-4 w-4 text-amber-700" /> Merge &amp; Amalgamate</DialogTitle>
          <DialogDescription>All references move to the survivor. Transaction history is preserved; only the duplicate master is removed.</DialogDescription>
        </DialogHeader>
        {!data ? <LoadingState label="Building merge preview…" /> : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="dd-merge-survivor">
                <div className="text-[10px] font-semibold uppercase text-emerald-700">Survivor (keep)</div>
                <div className="font-semibold">{data.survivor.name}</div>
                <div className="font-mono text-[10px] text-stone-500">{data.survivor.bizId || data.survivor.id}</div>
                <div className="mt-1 text-xs">Linked txns: <b>{data.survivor.linkedTransactions}</b></div>
              </div>
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="dd-merge-duplicate">
                <div className="text-[10px] font-semibold uppercase text-rose-700">Duplicate (merge in)</div>
                <div className="font-semibold">{data.duplicate.name}</div>
                <div className="font-mono text-[10px] text-stone-500">{data.duplicate.bizId || data.duplicate.id}</div>
                <div className="mt-1 text-xs">Linked txns: <b>{data.duplicate.linkedTransactions}</b></div>
              </div>
            </div>
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3" data-testid="dd-merge-after">
              <div className="text-[10px] font-semibold uppercase text-stone-500">After merge</div>
              <div className="mt-1 text-xs">Survivor ID <span className="font-mono">{data.afterMerge.survivorBizId || data.afterMerge.survivorId}</span> · Total linked transactions <b>{data.afterMerge.totalLinkedTransactions}</b></div>
            </div>
            <Input data-testid="dd-merge-reason" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} className="h-9 text-sm" />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dd-merge-cancel">Cancel</Button>
          <Button className="gap-1.5 bg-amber-700 hover:bg-amber-800" disabled={busy || !data} onClick={confirm} data-testid="dd-merge-confirm">
            <GitMerge className="h-4 w-4" /> Merge &amp; Amalgamate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupCard({ entity, group, onChanged }) {
  const [open, setOpen] = useState(false);
  const [survivor, setSurvivor] = useState(group.survivorId);
  const [merge, setMerge] = useState(null); // {duplicateId}
  const recs = group.records || [];
  const doDelete = async (recordId) => {
    try {
      const res = await api.post(`/dedup/${entity}/delete`, { recordId, reason: "duplicate cleanup" });
      toast.success("Duplicate deleted"); onChanged?.(res.data.audit);
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="dd-group">
      <button className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-stone-50/60" onClick={() => setOpen(!open)}>
        <div>
          <div className="font-semibold text-stone-800">{group.name}
            {group.gstin && <span className="ml-2 font-mono text-[10px] text-stone-400">{group.gstin}</span>}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-stone-500">
            <span>{group.count} records</span>
            {group.summary?.safeDelete > 0 && <Badge className="border-0 bg-sky-100 text-[10px] text-sky-700">{group.summary.safeDelete} delete</Badge>}
            {group.summary?.safeMerge > 0 && <Badge className="border-0 bg-amber-100 text-[10px] text-amber-700">{group.summary.safeMerge} merge</Badge>}
            {group.summary?.review > 0 && <Badge className="border-0 bg-rose-100 text-[10px] text-rose-700">{group.summary.review} review</Badge>}
          </div>
        </div>
        {open ? <ChevronDown className="h-4 w-4 text-stone-400" /> : <ChevronRight className="h-4 w-4 text-stone-400" />}
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-stone-100">
          <table className="w-full text-xs">
            <thead className="bg-stone-50 text-left text-[10px] uppercase text-stone-500">
              <tr><th className="px-3 py-2">Keep</th><th className="px-3 py-2">Record</th><th className="px-3 py-2">ID</th><th className="px-3 py-2">GSTIN/Unit</th><th className="px-3 py-2 text-right">Linked Txns</th><th className="px-3 py-2 text-right">Value</th><th className="px-3 py-2">Class</th><th className="px-3 py-2 text-right">Action</th></tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {recs.map((r) => {
                const isSurv = r.id === survivor;
                const [badgeCls, badgeLbl] = CLS[isSurv ? "SURVIVOR" : r.classification] || CLS.REVIEW;
                return (
                  <tr key={r.id} className={isSurv ? "bg-emerald-50/40" : "hover:bg-stone-50/60"} data-testid={`dd-rec-${r.id}`}>
                    <td className="px-3 py-2"><input type="radio" name={`surv-${group.name}`} checked={isSurv} onChange={() => setSurvivor(r.id)} data-testid={`dd-surv-${r.id}`} /></td>
                    <td className="px-3 py-2 font-medium text-stone-800">{r.name}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-stone-500">{r.supplierId || r.buyerId || r.ingredientId || r.productId || r.id}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">{r.gstin || r.unit || r.sku || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.linkedTransactions ?? 0}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(r.transactionValue)}</td>
                    <td className="px-3 py-2"><Badge className={`border-0 text-[10px] ${badgeCls}`}>{badgeLbl}</Badge>{r.conflict && <div className="mt-0.5 flex items-center gap-1 text-[10px] text-rose-600"><AlertTriangle className="h-3 w-3" />{r.conflict}</div>}</td>
                    <td className="px-3 py-2 text-right">
                      {isSurv ? <span className="text-[10px] text-stone-400">—</span>
                        : (r.linkedTransactions || 0) === 0
                          ? <Button size="sm" variant="outline" className="h-7 gap-1 border-sky-200 px-2 text-[11px] text-sky-700 hover:bg-sky-50" data-testid={`dd-delete-${r.id}`} onClick={() => doDelete(r.id)}><Trash2 className="h-3 w-3" /> Delete</Button>
                          : r.classification === "REVIEW" && !(survivor !== group.survivorId)
                            ? <Button size="sm" variant="outline" disabled className="h-7 px-2 text-[11px]" data-testid={`dd-review-${r.id}`}>Review</Button>
                            : <Button size="sm" variant="outline" className="h-7 gap-1 border-amber-200 px-2 text-[11px] text-amber-700 hover:bg-amber-50" data-testid={`dd-merge-${r.id}`} onClick={() => setMerge({ duplicateId: r.id })}><GitMerge className="h-3 w-3" /> Merge → survivor</Button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {merge && (
        <MergePreviewDialog entity={entity} survivorId={survivor} duplicateId={merge.duplicateId}
          open={!!merge} onOpenChange={(o) => !o && setMerge(null)} onDone={() => { setMerge(null); onChanged?.(); }} />
      )}
    </div>
  );
}

export default function MasterDedupTab({ entity, entityLabel }) {
  const { data, isLoading, refetch } = useResource(`/dedup/${entity}`);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = data || {};
  const s = d.summary || {};
  const groups = d.groups || [];

  const openResolve = async () => {
    setResolveOpen(true); setPlan(null);
    try { const { data } = await api.get(`/dedup/${entity}/resolve-preview`); setPlan(data); }
    catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); setResolveOpen(false); }
  };
  const runResolve = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/dedup/${entity}/resolve-all`, { reason: "Bulk safe duplicate cleanup" });
      toast.success(`Resolved ${res.data.resolvedCount} record(s) · ${res.data.after.requiresReview} still need review`);
      setResolveOpen(false); refetch();
    } catch (e) { toast.error(apiErrorMessage(e, "Resolve failed")); }
    finally { setBusy(false); }
  };

  if (isLoading) return <LoadingState label={`Scanning ${entityLabel}…`} />;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat tid="dd-sum-groups" label="Duplicate Groups" value={s.duplicateGroups ?? 0} tone={s.duplicateGroups ? "rose" : "stone"} />
        <Stat tid="dd-sum-records" label="Duplicate Records" value={s.duplicateRecords ?? 0} tone={s.duplicateRecords ? "rose" : "stone"} />
        <Stat tid="dd-sum-delete" label="Safe to Delete" value={s.safeToDelete ?? 0} tone="sky" />
        <Stat tid="dd-sum-merge" label="Safe to Merge" value={s.safeToMerge ?? 0} tone="amber" />
        <Stat tid="dd-sum-review" label="Requires Review" value={s.requiresReview ?? 0} tone="rose" />
      </div>
      {groups.length === 0 ? <EmptyState title={`No duplicate ${entityLabel}`} message="No suspected duplicates by the current rules." /> : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-stone-500">Pick a survivor per group, then Delete unused or Merge used duplicates into it. REVIEW items are skipped by bulk cleanup.</p>
            <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={!(s.safeToDelete || s.safeToMerge)} onClick={openResolve} data-testid="dd-resolve-all">
              <Wand2 className="h-3.5 w-3.5" /> Resolve All Safe Duplicates
            </Button>
          </div>
          <div className="space-y-2">{groups.map((g, i) => <GroupCard key={i} entity={entity} group={g} onChanged={() => refetch()} />)}</div>
        </>
      )}

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="dd-resolve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Resolve All Safe Duplicates</DialogTitle>
            <DialogDescription>Only safe actions run. Anything requiring review is skipped and left untouched.</DialogDescription>
          </DialogHeader>
          {!plan ? <LoadingState label="Building plan…" /> : (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between rounded-lg border border-sky-200 bg-sky-50 px-3 py-2"><span>Safe to delete (unused)</span><b data-testid="dd-plan-delete">{plan.counts.safeDelete}</b></div>
              <div className="flex justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"><span>Safe to merge</span><b data-testid="dd-plan-merge">{plan.counts.safeMerge}</b></div>
              <div className="flex justify-between rounded-lg border border-rose-200 bg-rose-50 px-3 py-2"><span>Requires review (skipped)</span><b data-testid="dd-plan-review">{plan.counts.requiresReview}</b></div>
              <p className="pt-1 text-xs text-stone-500">Resolve {plan.counts.safeDelete + plan.counts.safeMerge} safe record(s)? The audit re-runs automatically afterwards.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveOpen(false)} data-testid="dd-resolve-cancel">Cancel</Button>
            <Button className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={busy || !plan || (plan.counts.safeDelete + plan.counts.safeMerge) === 0} onClick={runResolve} data-testid="dd-resolve-confirm">
              <Wand2 className="h-4 w-4" /> Resolve {plan ? plan.counts.safeDelete + plan.counts.safeMerge : ""} Safe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
